import { Building2, Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useTenantStore,
  useActiveTenant,
  useActiveOrganizationId,
} from "@/stores/tenant-store";

interface TenantSelectorProps {
  collapsed?: boolean;
}

export function TenantSelector({ collapsed = false }: TenantSelectorProps) {
  const activeTenant = useActiveTenant();
  const activeOrganizationId = useActiveOrganizationId();
  const { tenants, organizations, setActiveTenant, setActiveOrganization } =
    useTenantStore();

  const tenantList = Object.values(tenants);
  const organizationList = Object.values(organizations).filter(
    (organization) => organization.tenantId === activeTenant?.id,
  );
  const activeOrganization = organizationList.find(
    (organization) => organization.id === activeOrganizationId,
  );
  const hasMultipleSelections =
    tenantList.length > 1 || organizationList.length > 1;

  // If no tenants, don't render
  if (tenantList.length === 0) {
    return null;
  }

  // If only one tenant, just show the name (no dropdown)
  if (!hasMultipleSelections) {
    return (
      <div
        className={cn("flex items-center gap-2", collapsed && "justify-center")}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
          {activeTenant?.name.charAt(0).toUpperCase() ?? "T"}
        </div>
        {!collapsed && (
          <span className="truncate text-sm font-medium">
            {activeOrganization?.name ?? activeTenant?.name ?? "Tenant"}
          </span>
        )}
      </div>
    );
  }

  // Multiple tenants: show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-auto p-2 hover:bg-accent",
            collapsed ? "w-full justify-center" : "w-full justify-between",
          )}
        >
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
              {activeTenant?.name.charAt(0).toUpperCase() ?? "T"}
            </div>
            {!collapsed && (
              <span className="truncate text-sm font-medium">
                {activeOrganization?.name ??
                  activeTenant?.name ??
                  "Select tenant"}
              </span>
            )}
          </div>
          {!collapsed && (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {tenantList.map((tenant) => (
          <DropdownMenuItem
            key={tenant.id}
            onClick={() => setActiveTenant(tenant.id, tenant)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary text-primary-foreground text-xs font-semibold">
                {tenant.name.charAt(0).toUpperCase()}
              </div>
              <span className="truncate">{tenant.name}</span>
            </div>
            {activeTenant?.id === tenant.id && (
              <Check className="h-4 w-4 shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
        {organizationList.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Organización</DropdownMenuLabel>
            {organizationList.map((organization) => (
              <DropdownMenuItem
                key={organization.id}
                onClick={() => setActiveOrganization(organization.id)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span className="truncate">{organization.name}</span>
                </div>
                {activeOrganizationId === organization.id && (
                  <Check className="h-4 w-4 shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
